"use client";

import React, { useState } from "react";
import Icon from "../icons/Icons";
import { motion, AnimatePresence } from "framer-motion";

interface LegalAccordionProps {
  htmlContent: string;
}

export default function LegalAccordion({ htmlContent }: LegalAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  if (!htmlContent) return null;

  // Clean the wrapper tags to prevent unclosed tags when splitting
  let cleanedHtml = htmlContent.trim();

  // Try to remove <section> and <div> wrappers if they exist at the ends
  cleanedHtml = cleanedHtml.replace(/^<section[^>]*>\s*/i, "");
  cleanedHtml = cleanedHtml.replace(/\s*<\/section>$/i, "");
  cleanedHtml = cleanedHtml.replace(/^<div[^>]*>\s*/i, "");
  cleanedHtml = cleanedHtml.replace(/\s*<\/div>$/i, "");

  const parts = cleanedHtml.split(/<h2[^>]*>/i);

  const items = parts.slice(1).map((part) => {
    const match = part.match(/^([\s\S]*?)<\/h2>([\s\S]*)/i);
    if (match) {
      return {
        title: match[1].replace(/<[^>]*>?/gm, "").trim(),
        content: match[2].trim(),
      };
    }
    return { title: "Section", content: part };
  });

  // If there are no h2 tags, we just render the content directly
  if (items.length === 0) {
    return (
      <div
        className="prose max-w-none text-primaryBlack text-xs xs:text-sm md:text-base font-light my-4 last-of-type:mb-0"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  }

  return (
    <div className="space-y-3.5 sm:space-y-5">
      {items.map((item, index) => {
        const isActive = openIndex === index;

        return (
          <div
            key={index}
            className={`border-2 rounded-xl px-4 sm:px-7 overflow-hidden transition ${
              isActive
                ? "bg-slate-50 border-[#1A51AA3D]"
                : "bg-white border-[#1A51AA3D]"
            }`}
          >
            <h2 aria-label={item.title} className="w-full m-0">
              <button
                onClick={() => setOpenIndex(isActive ? null : index)}
                className={[
                  "w-full flex justify-between items-center py-2.5 sm:py-5 text-sm xs:text-base md:text-lg font-medium transition text-left focus:outline-hidden",
                  isActive ? "text-primaryBlue" : "text-primaryBlack",
                ].join(" ")}
              >
                <span dangerouslySetInnerHTML={{ __html: item.title }} />
                <Icon.dropdownDownArrow
                  className={[
                    "transition w-4 h-4 ml-4 shrink-0",
                    isActive ? "rotate-180" : "",
                  ].join(" ")}
                />
              </button>
            </h2>

            <AnimatePresence initial={false}>
              {isActive && (
                <motion.div
                  key="content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="pb-3 sm:pb-4 pt-0">
                    <div
                      className="prose faq-content max-w-none text-primaryBlack text-xs xs:text-sm md:text-base font-light"
                      dangerouslySetInnerHTML={{ __html: item.content }}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
